import { useAuthStore } from '@/stores/auth.store';
import type { ApiResponse } from './types';

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? '/api/v1';
export const API_ORIGIN = new URL(BASE, window.location.origin).origin;

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }

  /** Field errors from a validation failure: { "lines.0.quantity": "..." } */
  get fieldErrors(): Record<string, string> {
    if (!Array.isArray(this.details)) return {};
    return Object.fromEntries(
      (this.details as { path?: string; message?: string }[]).filter((d) => d.path).map((d) => [d.path!, d.message ?? '']),
    );
  }
}

type QueryValue = string | number | boolean | null | undefined;
export type Query = Record<string, QueryValue>;

interface RequestOptions {
  query?: Query;
  body?: unknown;
  formData?: FormData;
  signal?: AbortSignal;
}

export function buildUrl(path: string, query?: Query) {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(query ?? {})) {
    if (v !== undefined && v !== null && v !== '') url.searchParams.set(k, String(v));
  }
  return url.pathname + url.search;
}

/* ------------------------------ Token refresh ------------------------------ */

let refreshing: Promise<string | null> | null = null;

/**
 * Exchanges the httpOnly refresh cookie for a new access token. Concurrent
 * callers share one request (single flight) so the refresh token is rotated once.
 */
export function refreshAccessToken(): Promise<string | null> {
  refreshing ??= fetch(`${BASE}/auth/refresh`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-Client': 'web' },
    body: '{}',
  })
    .then(async (res) => (res.ok ? ((await res.json()) as ApiResponse<{ accessToken: string }>).data.accessToken : null))
    .catch(() => null)
    .then((token) => {
      useAuthStore.getState().setAccessToken(token);
      return token;
    })
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

/* --------------------------------- Request --------------------------------- */

const GATEWAY_STATUSES = new Set([502, 503, 504]);

async function send(method: string, path: string, opts: RequestOptions, token: string | null) {
  const headers: Record<string, string> = { 'X-Client': 'web' };
  if (token) headers.Authorization = `Bearer ${token}`;
  let body: BodyInit | undefined;
  if (opts.formData) body = opts.formData;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  return fetch(buildUrl(path, opts.query), { method, headers, body, credentials: 'include', signal: opts.signal });
}

export async function request<T, M = unknown>(method: string, path: string, opts: RequestOptions = {}): Promise<ApiResponse<T, M>> {
  let res = await send(method, path, opts, useAuthStore.getState().accessToken);

  if (res.status === 401 && !path.startsWith('/auth/')) {
    const token = await refreshAccessToken();
    if (!token) {
      useAuthStore.getState().expire();
      throw new ApiError(401, 'SESSION_EXPIRED', 'انتهت الجلسة، يرجى تسجيل الدخول مجددًا');
    }
    res = await send(method, path, opts, token);
  }

  if (res.status === 204) return { data: undefined as T };
  const json = await res.json().catch(() => null);
  if (!res.ok) {
    // No API error body on a gateway failure: the proxy/load balancer could not reach the server.
    if (!json?.error && GATEWAY_STATUSES.has(res.status)) {
      throw new ApiError(res.status, 'SERVER_UNREACHABLE', 'تعذر الاتصال بالخادم، تأكد من تشغيله وحاول مجددًا');
    }
    const err = json?.error ?? {};
    throw new ApiError(res.status, err.code ?? 'HTTP_ERROR', err.message ?? 'حدث خطأ غير متوقع', err.details);
  }
  return json as ApiResponse<T, M>;
}

export const api = {
  get: <T, M = unknown>(path: string, query?: Query, signal?: AbortSignal) => request<T, M>('GET', path, { query, signal }),
  post: <T, M = unknown>(path: string, body?: unknown) => request<T, M>('POST', path, { body: body ?? {} }),
  patch: <T, M = unknown>(path: string, body?: unknown) => request<T, M>('PATCH', path, { body: body ?? {} }),
  put: <T, M = unknown>(path: string, body?: unknown) => request<T, M>('PUT', path, { body: body ?? {} }),
  delete: <T = void>(path: string) => request<T>('DELETE', path),
  upload: <T>(method: 'POST' | 'PATCH' | 'PUT', path: string, formData: FormData) => request<T>(method, path, { formData }),
};

/** Resolves a relative API file URL (signed photo links) against the current origin. */
export const fileUrl = (path: string | null | undefined) => (path ? new URL(path, window.location.origin).toString() : null);
